import type { ITransaction } from "@/data/model/Order";
import type { IMultiLanguageText } from "@/data/model/Shared";
import { DateTime } from "@/data/util/DateTime";
import { Format } from "@/data/util/Format";

import { Component } from "@/shared/elements/Component";
import { clientLanguage } from "@/shared/localization/language";

import { CancellationStateBadge } from "@/operator/elements/CancellationStateBadge";
import { operatorEvents } from "@/operator/events/operatorEvents";
import { trans } from "@/operator/localization/Trans";

import { PaymentStateBadge } from "./PaymentStateBadge";

export class PaymentInfoHeader extends Component {
    public readonly transaction: ITransaction;

    constructor(order: ITransaction) {
        super();
        this.transaction = order;
        this.$element = this.drawElement();
    }

    protected drawElement() {
        // draw order header fields
        const $element = $('<div class="payment-info-header" />');

        // 1. set summary row
        const $summaryRow = $('<div class="summary-row" />').appendTo($element);

        const $paymentInfo = $('<div class="payment-info" />').appendTo($summaryRow);
        // if the bill is open, make table name clickable
        if (this.hasOpenBill() && this.transaction.tableId != null) {
            $paymentInfo.append(`<div class="table with-open-bill">${this.getTableName()}</div>`);
            $paymentInfo.find(".table.with-open-bill").click(() => {
                operatorEvents.transactionDetailsWindow.openBillTableSelected.dispatch({
                    tableId: this.transaction.tableId!,
                    billId: this.transaction.billId!,
                });
            });
        } else {
            $paymentInfo.append(`<div class="table">${this.getTableName()}</div>`);
        }

        // Set waiter or end user name
        if (this.transaction.userId == null) {
            $paymentInfo.append(`<div class="waiter">${trans.general.deletedUser}</div>`);
        } else {
            if (this.transaction.userName === "Anonymer User") {
                this.transaction.userName = trans.orders.anonymousUser;
            }

            if (this.transaction.userName != null && this.transaction.userName !== "") {
                $paymentInfo.append(`<div class="waiter">${this.transaction.userName}</div>`);
            }
        }

        // set order time info
        const $timeInfo = $('<div class="payment-time-info" />').appendTo($summaryRow);
        $timeInfo.append(
            `<div class="time">${this.getPaymentTime()}</div>` + `<div class="date">${this.getPaymentDate()}</div>`
        );

        // 2. set statues row
        const $statusesRow = $('<dic class="statuses-row" />').appendTo($element);
        $("<div></div>").appendTo($statusesRow);

        // set payment states info
        const $paymentState = $(`<div class="payment-states" />`).appendTo($statusesRow);
        if (this.transaction.payUseCaseName != null) {
            const $useCase = $('<div class="use-case" />').appendTo($paymentState);
            $useCase.append(`<span>${this.getUseCaseName(this.transaction.payUseCaseName)}</span>`);
        }

        PaymentStateBadge.renderTo($paymentState, this.transaction.state);

        if (this.transaction.cancellationState != null) {
            CancellationStateBadge.renderTo($paymentState, this.transaction.cancellationState);
        }

        return $element;
    }

    private getTableName(): string | undefined {
        const tableName = this.transaction.tableName;
        const customTableName = this.transaction.customTableName;

        if (tableName === null) {
            return trans.production.deletedTable;
        }

        return Format.htmlEscape(customTableName);
    }

    private getPaymentTime(): string {
        return DateTime.toTimeString(this.transaction.confirmDate ?? this.transaction.created, true);
    }

    private getPaymentDate(): string {
        return DateTime.toDateString(this.transaction.confirmDate ?? this.transaction.created);
    }

    private getUseCaseName(name: IMultiLanguageText): string | undefined {
        return Format.htmlEscape(Format.translate(name, clientLanguage()));
    }

    private hasOpenBill() {
        return this.transaction.isBillOpen && this.transaction.billId != null && this.transaction.tableId != null;
    }

    public static render($appendToTarget: JQuery, order: ITransaction): PaymentInfoHeader {
        const component = new PaymentInfoHeader(order);
        component.renderTo($appendToTarget);

        return component;
    }
}
